export const tsmtPitchTherapists = {
  hero: {
    eyebrow: "Bespire × TSMT®",
    title: "Give TSMT® therapists an AI assistant that actually takes work off their plate.",
    subtitle:
      "Website, booking, marketing, invoicing, reminders — in one chat. Cheaper than separate tools, faster than any agency.",
    tags: ["Booking", "Campaign management", "Website", "AI assistant"],
  },
  pain: {
    title: "The therapist's biggest pain in the ass.",
    subtitle: "We hear this from every TSMT® therapist we talk to.",
    items: [
      { title: "Booking", description: "Phone, message, calendar — tracked in three places, things still slip through." },
      { title: "Campaign management", description: "Should be advertising, should be posting — but when? About what? How to measure?" },
      { title: "Invoicing and payments", description: "Separate software, separate login, manual reconciliation, late payments." },
      { title: "Reminders", description: "Cancelled slots, empty hours, forgotten follow-ups — everyone loses." },
      { title: "Targeted messages", description: "One parent group needs one update, another needs something else — no time to send separately." },
      { title: "Team", description: "Can't afford an assistant, no time to learn AI. Left alone." },
    ],
  },
  story: {
    eyebrow: "Why we're here",
    title: "We've built this with therapists from day one.",
    paragraphs: [
      "Not by accident. There's personal involvement behind it — we have a TSMT® therapist in the family, so we know exactly what the weekly grind, 10-minute windows and weekend admin look like.",
      "The goal is clear: make the AI that big companies have used for years available to individual and small SMB service providers too. Help them sell more and run smoother — so they can be more successful.",
      "TSMT® therapists are our textbook ICP: strong professionals, no time, with most of their value eaten by admin today.",
    ],
  },
  evolution: {
    title: "We replace a whole back office in three steps.",
    subtitle: "Same chat, taking on more work over time. Cheaper and more effective than separate tools and separate people.",
    steps: [
      { step: "01", title: "Replace the website builder", description: "The therapist describes their practice in chat — and gets a finished, bookable, payable website. No Wix, no developer, no months-long project." },
      { step: "02", title: "Replace the marketer", description: "The AI writes posts, schedules them, builds campaigns and measures what works. The therapist approves — doesn't produce content." },
      { step: "03", title: "Personal assistant", description: "Scheduling, reminders, invoicing, messaging, team coordination — all in chat, in natural language. One place, one surface." },
    ],
    footer: "All in one AI assistant, via chat. More effective — and cheaper than today's separate tools combined.",
  },
  orsolyaCase: {
    eyebrow: "What it could look like for you",
    title: "Sági-Guth Orsolya — a concrete flow, step by step.",
    subtitle: "How the AI assistant builds, launches and runs her practice day to day.",
    phases: [
      {
        label: "1. Website in a chat",
        title: "The AI builds Orsolya's own site based on the TSMT® page.",
        bullets: [
          "Intro, specialties, locations — asked via chat",
          "TSMT® therapist badge, links to the official methodology",
          "Own subdomain or custom domain — in minutes",
        ],
      },
      {
        label: "2. Booking",
        title: "Booking built in, not in a separate tool.",
        bullets: [
          "Locations, durations, breaks, days off — set up in chat",
          "Online payment or on-site — Orsolya decides per session",
          "Automatic reminders via email and WhatsApp",
        ],
      },
      {
        label: "3. Digital products",
        title: "Home exercises, courses, packages — sold.",
        bullets: [
          "Video home-exercise pack for parents",
          "Online parent consultation paid up front",
          "Workshop tickets, series passes",
        ],
      },
      {
        label: "4. The rest, handled",
        title: "Invoicing, reminders, messages — automatic.",
        bullets: [
          "Invoice issued automatically after payment",
          "Follow-up reminder to the right parent at the right time",
          "Targeted group messages: only to the parents involved",
        ],
      },
    ],
  },
  dailyFlow: {
    title: "What a week with it looks like.",
    subtitle: "Everything in the same chat. Not 7 tabs, not 4 apps.",
    items: [
      { title: "Adding a new appointment", description: "\"Put Laci Kovács in Wednesday 3pm, 45-minute block.\" — done." },
      { title: "Promoting a slot", description: "\"I opened a new Thursday afternoon slot, post it and notify the waitlist.\"" },
      { title: "Daily operations", description: "Morning chat summary: who's coming, who paid, who to call back." },
      { title: "Payments & invoicing", description: "Online card, Apple Pay, on-site — invoice goes to the parent automatically." },
      { title: "Reminders", description: "24h before, 2h before — email and WhatsApp, parent-friendly." },
      { title: "Targeted messages", description: "\"Send the parents needing a follow-up that I have January openings.\"" },
      { title: "Team", description: "If there's an assistant or colleague, they work in the same system — with roles." },
      { title: "Not just AI", description: "Human support behind it. In Hungarian. Someone picks up when things break." },
    ],
  },
  integration: {
    eyebrow: "TSMT® integration",
    title: "Bookings flow automatically into LTSZ.",
    subtitle: "The point: after a Bespire booking, the same LTSZ flow continues as today — just automated. No manual re-entry for the therapist, saved time, minimized risk of mistakes.",
    bullets: [
      "Booked appointments are pushed into LTSZ automatically — no manual transfer",
      "The familiar LTSZ flow continues; only the front of the booking is automated",
      "Time saved for the therapist: no double-entry across two systems",
      "Minimized error risk — no mistyped names, missed entries, or double bookings",
    ],
  },
  partnership: {
    title: "The partnership flywheel.",
    subtitle: "Each step pulls the next one for both sides.",
    steps: [
      "Discounted Bespire package + free training for TVTH members",
      "Expanded therapist profile on the TSMT® site with a link to the Bespire page",
      "More sales for the therapist (booking, digital products, courses)",
      "Higher software usage → higher LTSZ revenue",
      "Therapist gets more visible value out of TVTH membership",
      "Bespire ↔ TSMT® integration shipped into the system",
      "TSMT® customer stream → new therapists onboard more easily, even through Bespire",
      "Also useful for international expansion",
    ],
  },
  education: {
    eyebrow: "Education",
    title: "We hold their hand — like you do.",
    subtitle: "Education is TSMT®'s strength. We bring the same posture to the online side.",
    items: [
      { title: "Online workshops", description: "Weekly / bi-weekly live online training on using Bespire, in Hungarian." },
      { title: "Onboarding companion", description: "Building the first site and first booking together — we don't leave them alone." },
      { title: "Free for TVTH members", description: "Training becomes part of TVTH membership — added value for the fee." },
      { title: "Knowledge base & templates", description: "TSMT®-specific site templates, post templates, booking patterns." },
    ],
  },
  pricing: {
    eyebrow: "Pricing",
    title: "Discounted package for TSMT® therapists.",
    subtitle: "Two models on the table — we pick the right one together.",
    plans: [
      {
        name: "Model A — Tiered discount",
        highlight: "40% in year one, 20% in year two",
        description: "Long-term commitment, gradually rising usage. The therapist beds in before standard pricing kicks in.",
        bullets: [
          "−40% discount for the first 12 months",
          "−20% discount for the second 12 months",
          "Standard pricing from year three",
        ],
      },
      {
        name: "Model B — One-time entry",
        highlight: "50% in year one",
        description: "Simpler message, faster decision. Ideal for pilot therapists.",
        bullets: [
          "−50% discount for the first 12 months",
          "Standard pricing from year two",
          "Easier to communicate towards TVTH",
        ],
      },
    ],
    footerHighlight: {
      badge: "TVTH membership perk",
      title: "Training is fully free for TVTH members in both models.",
      description: "For a non-TVTH member, the same training package costs HUF 50,000 + VAT — for members it's zero, included with the membership.",
    },
  },
  nextSteps: {
    eyebrow: "Next steps",
    title: "Quick pilot, then announce at the July conference.",
    items: [
      { step: "01", title: "3–5 therapists — you pick them", description: "People you see momentum in and where there's something to show. We stand next to each of them." },
      { step: "02", title: "Quick pilot test", description: "In a few weeks we build the sites, launch booking, train them. We measure: bookings, revenue, retention." },
      { step: "03", title: "Announcement — July 3, 2026", description: "If the pilot is successful, we jointly announce the partnership and the TVTH-member package at the TSMT® conference." },
    ],
  },
  closing: {
    eyebrow: "Our offer",
    title: "Let's build the back office for TSMT® therapists — together.",
    lede: "We're not selling software. We're proposing a partnership: TSMT® gets stronger, TVTH membership becomes visibly more valuable, therapists win back 10–15 hours a week — and meanwhile we build something that scales internationally.",
    askLabel: "What we ask from you",
    asks: [
      "Pick 3–5 therapists for the pilot — you know who has the momentum",
      "A shared working track for the coming weeks — fast feedback, fast decisions",
      "Joint announcement at the TSMT® conference on July 3, 2026, if the pilot lands",
    ],
    giveLabel: "What TSMT® gets in return",
    gives: [
      "Bespire ↔ LTSZ integration — bookings flow into the system automatically",
      "Discounted package + free training for TVTH members — tangible membership value",
      "Higher LTSZ usage, a more visible TSMT® brand, and a new therapist stream via Bespire",
    ],
    cta: {
      label: "Next step",
      description: "A 30-minute conversation to pick the pilot therapists together and lock the roadmap up to the July conference.",
      buttonLabel: "Book a slot",
      contact: "zoltan@bespire.io",
    },
    signoff: "Thank you for hearing us out. The next move is yours — we're ready.",
  },
};
